import React, { useState, useEffect } from "react";
import authSet from "../hooks/useAuth.js";
import { Link } from "react-router-dom";
import Navbar from "./Navbar.js";
import Footer from "./Footer.js";

const Pricing = () => {
  const { isAuthenticated } = authSet();
  
  // Currency detection - default to USD unless timezone suggests Nigeria
  const [currency, setCurrency] = useState(() => {
    try {
      const timezone = Intl.DateTimeFormat().resolvedOptions().timeZone;
      // Auto-detect Nigerian timezone (Africa/Lagos) or manual selection from localStorage
      const savedCurrency = localStorage.getItem('preferredCurrency');
      if (savedCurrency) return savedCurrency;
      return timezone === 'Africa/Lagos' ? 'NGN' : 'USD';
    } catch {
      return 'USD';
    }
  });

  useEffect(() => {
    // Save currency preference
    localStorage.setItem('preferredCurrency', currency);
  }, [currency]);

  const toggleCurrency = () => {
    setCurrency(prev => prev === 'USD' ? 'NGN' : 'USD');
  };

  // Price formatting
  const formatPrice = (usdPrice, ngnPrice) => {
    if (currency === 'NGN') {
      return `₦${Number(ngnPrice).toLocaleString('en-NG')}`;
    }
    return `$${usdPrice}`;
  };

  return (
    <div className="bg-gray-50">
      <Navbar isAuthenticated={isAuthenticated} />
      {/* Hero Section */}
      <section className="page-header py-16">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h1 className="text-2xl font-medium text-white sm:text-5xl">
            Simple, Transparent Pricing
          </h1>
          <p className="mt-4 text-xl text-gray-200 max-w-3xl mx-auto">
            Start free and scale as your savings grow. No hidden fees, just
            clear value.
          </p>
          
          {/* Currency Toggle */}
          <div className="mt-6 inline-flex items-center bg-white/10 backdrop-blur-sm rounded-lg p-1">
            <button
              onClick={toggleCurrency}
              className={`px-4 py-2 rounded-md text-sm font-medium transition-all ${
                currency === 'USD' 
                  ? 'bg-white text-gray-900 shadow-sm' 
                  : 'text-white hover:text-gray-200'
              }`}
            >
              USD ($)
            </button>
            <button
              onClick={toggleCurrency}
              className={`px-4 py-2 rounded-md text-sm font-medium transition-all ${
                currency === 'NGN' 
                  ? 'bg-white text-gray-900 shadow-sm' 
                  : 'text-white hover:text-gray-200'
              }`}
            >
              NGN (₦)
            </button>
          </div>
        </div>
      </section>

      {/* Pricing Plans */}
      <section className="py-16">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 gap-8 lg:grid-cols-3">
            {/* Free Plan */}
            <div className="bg-white rounded-xl shadow-lg overflow-hidden border border-gray-200">
              <div className="p-8">
                <div className="flex items-center justify-between">
                  <h3 className="text-2xl font-medium text-gray-900">Free</h3>
                  <span className="inline-flex items-center px-3 py-1 rounded-full text-sm font-medium bg-gray-100 text-gray-800">
                    Basic
                  </span>
                </div>
                <p className="mt-2 text-gray-600">Power-up your group</p>

                <div className="mt-6">
                  <span className="text-2xl font-medium text-gray-900">$0</span>
                  <span className="text-lg font-medium text-gray-500">
                    /forever
                  </span>
                </div>

                <ul className="mt-8 space-y-4">
                  <li className="flex items-start">
                    <svg
                      className="h-6 w-6 text-green-500 flex-shrink-0"
                      fill="none"
                      viewBox="0 0 24 24"
                      stroke="currentColor"
                    >
                      <path
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        strokeWidth="2"
                        d="M5 13l4 4L19 7"
                      />
                    </svg>
                    <span className="ml-3 text-gray-600">
                      Up to 5 members per thrift
                    </span>
                  </li>
                  <li className="flex items-start">
                    <svg
                      className="h-6 w-6 text-green-500 flex-shrink-0"
                      fill="none"
                      viewBox="0 0 24 24"
                      stroke="currentColor"
                    >
                      <path
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        strokeWidth="2"
                        d="M5 13l4 4L19 7"
                      />
                    </svg>
                    <span className="ml-3 text-gray-600">
                      Up to 3 active thrifts
                    </span>
                  </li>
                  <li className="flex items-start">
                    <svg
                      className="h-6 w-6 text-green-500 flex-shrink-0"
                      fill="none"
                      viewBox="0 0 24 24"
                      stroke="currentColor"
                    >
                      <path
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        strokeWidth="2"
                        d="M5 13l4 4L19 7"
                      />
                    </svg>
                    <span className="ml-3 text-gray-600">
                      Up to 20 members per group
                    </span>
                  </li>
                  <li className="flex items-start">
                    <svg
                      className="h-6 w-6 text-green-500 flex-shrink-0"
                      fill="none"
                      viewBox="0 0 24 24"
                      stroke="currentColor"
                    >
                      <path
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        strokeWidth="2"
                        d="M5 13l4 4L19 7"
                      />
                    </svg>
                    <span className="ml-3 text-gray-600">
                      Up to 3 active groups
                    </span>
                  </li>
                  <li className="flex items-start">
                    <svg
                      className="h-6 w-6 text-green-500 flex-shrink-0"
                      fill="none"
                      viewBox="0 0 24 24"
                      stroke="currentColor"
                    >
                      <path
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        strokeWidth="2"
                        d="M5 13l4 4L19 7"
                      />
                    </svg>
                    <span className="ml-3 text-gray-600">
                      Manual payment tracking
                    </span>
                  </li>
                  <li className="flex items-start">
                    <svg
                      className="h-6 w-6 text-green-500 flex-shrink-0"
                      fill="none"
                      viewBox="0 0 24 24"
                      stroke="currentColor"
                    >
                      <path
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        strokeWidth="2"
                        d="M5 13l4 4L19 7"
                      />
                    </svg>
                    <span className="ml-3 text-gray-600">
                      Basic notifications
                    </span>
                  </li>
                  <li className="flex items-start">
                    <svg
                      className="h-6 w-6 text-green-500 flex-shrink-0"
                      fill="none"
                      viewBox="0 0 24 24"
                      stroke="currentColor"
                    >
                      <path
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        strokeWidth="2"
                        d="M5 13l4 4L19 7"
                      />
                    </svg>
                    <span className="ml-3 text-gray-600">
                      Community support
                    </span>
                  </li>
                </ul>

                <div className="mt-8">
                  <Link
                    to="/register"
                    className="block w-full py-3 px-6 text-center rounded-lg text-white font-medium bg-gray-600 hover:bg-gray-700"
                  >
                    Get Started Free
                  </Link>
                </div>

                <div className="mt-6 text-center text-sm text-gray-500">
                  Try Ajo free and get unrestricted access to all our products
                  and features.
                </div>
              </div>
            </div>

            {/* Pro Plan */}
            <div className="bg-white rounded-xl shadow-lg overflow-hidden border-2 border-gray-600 transform scale-105 z-10">
              <div className="p-8">
                <div className="flex items-center justify-between">
                  <h3 className="text-2xl font-medium text-gray-900">
                    Pro Plan
                  </h3>
                  <span className="inline-flex items-center px-3 py-1 rounded-full text-sm font-medium bg-green-100 text-green-800">
                    Popular
                  </span>
                </div>
                <p className="mt-2 text-gray-600">Perfect for active savers</p>

                <div className="mt-6">
                  <span className="text-2xl font-medium text-gray-900">
                    {formatPrice('9.99', '2500')}
                  </span>
                  <span className="text-lg font-medium text-gray-500">
                    /month
                  </span>
                  <p className="text-sm text-gray-500">
                    {currency === 'NGN' 
                      ? 'Billed annually at ₦25,000 (Save 17%)'
                      : 'Billed annually at $99.99 (Save 17%)'
                    }
                  </p>
                </div>

                <ul className="mt-8 space-y-4">
                  <li className="flex items-start">
                    <svg
                      className="h-6 w-6 text-green-500 flex-shrink-0"
                      fill="none"
                      viewBox="0 0 24 24"
                      stroke="currentColor"
                    >
                      <path
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        strokeWidth="2"
                        d="M5 13l4 4L19 7"
                      />
                    </svg>
                    <span className="ml-3 text-gray-600">
                      Up to 20 members per thrift
                    </span>
                  </li>
                  <li className="flex items-start">
                    <svg
                      className="h-6 w-6 text-green-500 flex-shrink-0"
                      fill="none"
                      viewBox="0 0 24 24"
                      stroke="currentColor"
                    >
                      <path
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        strokeWidth="2"
                        d="M5 13l4 4L19 7"
                      />
                    </svg>
                    <span className="ml-3 text-gray-600">
                      Unlimited thrift creation
                    </span>
                  </li>
                  <li className="flex items-start">
                    <svg
                      className="h-6 w-6 text-green-500 flex-shrink-0"
                      fill="none"
                      viewBox="0 0 24 24"
                      stroke="currentColor"
                    >
                      <path
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        strokeWidth="2"
                        d="M5 13l4 4L19 7"
                      />
                    </svg>
                    <span className="ml-3 text-gray-600">
                      Up to 100 members per group
                    </span>
                  </li>
                  <li className="flex items-start">
                    <svg
                      className="h-6 w-6 text-green-500 flex-shrink-0"
                      fill="none"
                      viewBox="0 0 24 24"
                      stroke="currentColor"
                    >
                      <path
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        strokeWidth="2"
                        d="M5 13l4 4L19 7"
                      />
                    </svg>
                    <span className="ml-3 text-gray-600">
                      Unlimited group creation
                    </span>
                  </li>
                  <li className="flex items-start">
                    <svg
                      className="h-6 w-6 text-green-500 flex-shrink-0"
                      fill="none"
                      viewBox="0 0 24 24"
                      stroke="currentColor"
                    >
                      <path
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        strokeWidth="2"
                        d="M5 13l4 4L19 7"
                      />
                    </svg>
                    <span className="ml-3 text-gray-600">
                      Automatic payment processing
                    </span>
                  </li>
                  <li className="flex items-start">
                    <svg
                      className="h-6 w-6 text-green-500 flex-shrink-0"
                      fill="none"
                      viewBox="0 0 24 24"
                      stroke="currentColor"
                    >
                      <path
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        strokeWidth="2"
                        d="M5 13l4 4L19 7"
                      />
                    </svg>
                    <span className="ml-3 text-gray-600">
                      Advanced analytics & reports
                    </span>
                  </li>
                  <li className="flex items-start">
                    <svg
                      className="h-6 w-6 text-green-500 flex-shrink-0"
                      fill="none"
                      viewBox="0 0 24 24"
                      stroke="currentColor"
                    >
                      <path
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        strokeWidth="2"
                        d="M5 13l4 4L19 7"
                      />
                    </svg>
                    <span className="ml-3 text-gray-600">Priority support</span>
                  </li>
                </ul>

                <div className="mt-8">
                  <Link
                    to="/register"
                    className="block w-full py-3 px-6 text-center rounded-lg text-white font-medium bg-gray-600 hover:bg-gray-700"
                  >
                    Get Started
                  </Link>
                </div>

                <div className="mt-6 text-center text-sm text-gray-500">
                  Try Ajo free and get unrestricted access to all our products
                  and features.
                </div>
              </div>
            </div>

            {/* Enterprise Plan */}
            <div className="bg-white rounded-xl shadow-lg overflow-hidden border border-gray-200">
              <div className="p-8">
                <div className="flex items-center justify-between">
                  <h3 className="text-2xl font-medium text-gray-900">
                    Enterprise
                  </h3>
                  <span className="inline-flex items-center px-3 py-1 rounded-full text-sm font-medium bg-indigo-100 text-purple-800">
                    Business
                  </span>
                </div>
                <p className="mt-2 text-gray-600">
                  For organizations & large groups
                </p>

                <div className="mt-6">
                  <span className="text-2xl font-medium text-gray-900">
                    {formatPrice('49', '10000')}
                  </span>
                  <span className="text-lg font-medium text-gray-500">
                    /month
                  </span>
                  <p className="text-sm text-gray-500">
                    {currency === 'NGN'
                      ? 'Billed annually at ₦100,000 (Save 17%)'
                      : 'Billed annually at $490 (Save 17%)'
                    }
                  </p>
                </div>

                <ul className="mt-8 space-y-4">
                  <li className="flex items-start">
                    <svg
                      className="h-6 w-6 text-green-500 flex-shrink-0"
                      fill="none"
                      viewBox="0 0 24 24"
                      stroke="currentColor"
                    >
                      <path
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        strokeWidth="2"
                        d="M5 13l4 4L19 7"
                      />
                    </svg>
                    <span className="ml-3 text-gray-600">
                      Unlimited members & thrifts
                    </span>
                  </li>
                  <li className="flex items-start">
                    <svg
                      className="h-6 w-6 text-green-500 flex-shrink-0"
                      fill="none"
                      viewBox="0 0 24 24"
                      stroke="currentColor"
                    >
                      <path
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        strokeWidth="2"
                        d="M5 13l4 4L19 7"
                      />
                    </svg>
                    <span className="ml-3 text-gray-600">
                      Unlimited group members & groups
                    </span>
                  </li>
                  <li className="flex items-start">
                    <svg
                      className="h-6 w-6 text-green-500 flex-shrink-0"
                      fill="none"
                      viewBox="0 0 24 24"
                      stroke="currentColor"
                    >
                      <path
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        strokeWidth="2"
                        d="M5 13l4 4L19 7"
                      />
                    </svg>
                    <span className="ml-3 text-gray-600">
                      White-label customization
                    </span>
                  </li>
                  <li className="flex items-start">
                    <svg
                      className="h-6 w-6 text-green-500 flex-shrink-0"
                      fill="none"
                      viewBox="0 0 24 24"
                      stroke="currentColor"
                    >
                      <path
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        strokeWidth="2"
                        d="M5 13l4 4L19 7"
                      />
                    </svg>
                    <span className="ml-3 text-gray-600">
                      Advanced reporting & analytics
                    </span>
                  </li>
                  <li className="flex items-start">
                    <svg
                      className="h-6 w-6 text-green-500 flex-shrink-0"
                      fill="none"
                      viewBox="0 0 24 24"
                      stroke="currentColor"
                    >
                      <path
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        strokeWidth="2"
                        d="M5 13l4 4L19 7"
                      />
                    </svg>
                    <span className="ml-3 text-gray-600">
                      Dedicated account manager
                    </span>
                  </li>
                  <li className="flex items-start">
                    <svg
                      className="h-6 w-6 text-green-500 flex-shrink-0"
                      fill="none"
                      viewBox="0 0 24 24"
                      stroke="currentColor"
                    >
                      <path
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        strokeWidth="2"
                        d="M5 13l4 4L19 7"
                      />
                    </svg>
                    <span className="ml-3 text-gray-600">
                      API access & integrations
                    </span>
                  </li>
                </ul>

                <div className="mt-8">
                  <Link
                    to="/contact"
                    className="block w-full py-3 px-6 text-center rounded-lg text-white font-medium bg-gray-600 hover:bg-gray-700"
                  >
                    Contact Sales
                  </Link>
                </div>

                <div className="mt-6 text-center text-sm text-gray-500">
                  Try Ajo free and get unrestricted access to all our products
                  and features.
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Pricing Info Section */}
      <section className="py-12 bg-white">
        <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8">

          {/* Group Funding Info */}
          <div className="bg-gradient-to-br from-purple-50 to-pink-50 rounded-xl p-8 border border-purple-100">
            <div className="flex items-start">
              <div className="flex-shrink-0">
                <svg className="h-6 w-6 text-purple-600" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M17 20h5v-2a3 3 0 00-5.356-1.857M17 20H7m10 0v-2c0-.656-.126-1.283-.356-1.857M7 20H2v-2a3 3 0 015.356-1.857M7 20v-2c0-.656.126-1.283.356-1.857m0 0a5.002 5.002 0 019.288 0M15 7a3 3 0 11-6 0 3 3 0 016 0zm6 3a2 2 0 11-4 0 2 2 0 014 0zM7 10a2 2 0 11-4 0 2 2 0 014 0z" />
                </svg>
              </div>
              <div className="ml-4">
                <h3 className="text-lg font-medium text-gray-900 mb-2">
                  👥 What are Groups?
                </h3>
                <p className="text-sm text-gray-600 mb-3">
                  In addition to traditional thrift circles, Ajo supports <strong>Group Funding</strong> - a flexible way to pool money 
                  with friends, family, or colleagues for any shared goal (trips, gifts, events, etc.).
                </p>
                <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mt-4">
                  <div className="bg-white/60 rounded-lg p-3">
                    <p className="text-xs font-semibold text-gray-900 mb-1">Free Plan</p>
                    <p className="text-xs text-gray-600">• Up to 20 members/group<br />• 3 active groups total</p>
                  </div>
                  <div className="bg-white/60 rounded-lg p-3">
                    <p className="text-xs font-semibold text-gray-900 mb-1">Pro Plan</p>
                    <p className="text-xs text-gray-600">• Up to 100 members/group<br />• Unlimited groups</p>
                  </div>
                  <div className="bg-white/60 rounded-lg p-3">
                    <p className="text-xs font-semibold text-gray-900 mb-1">Enterprise</p>
                    <p className="text-xs text-gray-600">• Unlimited members/group<br />• Unlimited groups</p>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Fee Structure */}
      <section className="py-12 bg-gray-25">
        <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="bg-white rounded-lg p-8 shadow-sm border border-gray-100">
            <div className="text-center">
              <h3 className="text-base font-medium text-gray-600 mb-1">
                Setup Fee:{" "}
                <span className="text-xl font-medium text-green-600">Free</span>
              </h3>
            </div>

            <div className="mt-6 pt-6 border-t border-gray-100">
              <p className="text-sm text-gray-500 text-center max-w-xl mx-auto">
                AjoApp is authorized, and regulated by the Financial
                Transactions and Report Analysis Centre of Canada (FINTRAC) to
                provide money collection and transfer services as a Money
                Service Business.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="bg-gray-50 py-16">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h2 className="text-3xl font-medium text-gray-900 sm:text-2xl">
            Start saving with your group today
          </h2>
          <p className="mt-4 text-xl text-gray-600 max-w-3xl mx-auto">
            Whether you're saving with friends, family, or coworkers, our
            platform makes it simple to set up, manage, and track contributions
            so you can focus on reaching your goals together.
          </p>
          <div className="mt-8">
            <Link
              to="/register"
              className="inline-flex items-center px-6 py-3 border border-transparent text-base font-medium rounded-md shadow-sm text-white bg-gray-600 hover:bg-gray-700"
            >
              Start Saving Today
            </Link>
          </div>
        </div>
      </section>

      {/* Features Section */}
      <section className="py-16 w-3/4 mx-auto">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="lg:text-center">
            <h2 className="text-3xl font-medium text-gray-900 sm:text-2xl">
              Simple and Powerful Features
            </h2>
          </div>
          <div className="mt-10 grid grid-cols-1 gap-10 sm:grid-cols-2">
            <div className="bg-gray-50 p-6 rounded-lg">
              <h3 className="text-lg font-medium text-gray-900">
                Register and invite others in seconds
              </h3>
              <p className="mt-2 text-gray-600">
                Get your thrift group up and running quickly with our intuitive
                setup process.
              </p>
            </div>
            <div className="bg-gray-50 p-6 rounded-lg">
              <h3 className="text-lg font-medium text-gray-900">
                Confirm and receive reminders via secure portal
              </h3>
              <p className="mt-2 text-gray-600">
                Stay on top of contributions with automated reminders and easy
                confirmation.
              </p>
            </div>
          </div>
        </div>
      </section>
      <Footer />
    </div>
  );
};

export default Pricing;
